import { isCurrencyCode, isoMinorUnitDigits, type CurrencyCode } from "./currency";
import type { Money } from "./money";

/**
 * Display formatting for AR and EN. Deterministic and ICU-independent on purpose (browser,
 * Node and CI render byte-identical strings): Latin digits in both locales (ADR-0006,
 * `ar-u-nu-latn`), "," thousands separator, "." decimal separator, always the currency's full
 * minor-unit digits (SAR 2, BHD 3).
 *
 * - en: `SAR 1,234.50`, `-BHD 0.125`
 * - ar: `1,234.50 ر.س`, `-0.125 د.ب` (the number keeps LTR digit order; wrap the result in
 *   `<bdi>` inside RTL text)
 */
export type MoneyLocale = "ar" | "en";

export interface FormatMoneyOptions {
  /** `symbol` (default): localized short symbol in Arabic, ISO code in English; `code`: ISO. */
  readonly display?: "symbol" | "code";
  /** Thousands separators (default true). */
  readonly grouping?: boolean;
}

const AR_SYMBOL: Readonly<Record<CurrencyCode, string>> = { SAR: "ر.س", BHD: "د.ب" };

function group(integerDigits: string): string {
  return integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function currencyLabel(
  currency: CurrencyCode,
  locale: MoneyLocale,
  display: "symbol" | "code" = "symbol",
): string {
  return locale === "ar" && display === "symbol" ? AR_SYMBOL[currency] : currency;
}

export function formatMoney(
  value: Money,
  locale: MoneyLocale,
  options: FormatMoneyOptions = {},
): string {
  return formatMinorUnits(value.amountMinor, value.currency, locale, options);
}

export interface FormatMinorUnitsOptions extends FormatMoneyOptions {
  /** Fraction digits; default: the currency's ISO minor-unit digits (SAR 2, BHD 3). */
  readonly fractionDigits?: number;
  /** Where the label goes in English: `prefix` (default, `SAR 1.00`) or `suffix` (`1.00 SAR`). Arabic is always suffix. */
  readonly labelPlacement?: "prefix" | "suffix";
  /** `latin` (default) or `arabic-indic` digits with `٬` / `٫` separators. */
  readonly digits?: "latin" | "arabic-indic";
}

const ARABIC_INDIC = "٠١٢٣٤٥٦٧٨٩";

/**
 * Formats integer minor units for any ISO code (also currencies the platform does not bill in,
 * which keep their ISO code as label). Pure bigint/string math, no float.
 */
export function formatMinorUnits(
  amountMinor: bigint,
  currency: string,
  locale: MoneyLocale,
  options: FormatMinorUnitsOptions = {},
): string {
  const fractionDigits = options.fractionDigits ?? isoMinorUnitDigits(currency);
  const negative = amountMinor < 0n;
  const raw = (negative ? -amountMinor : amountMinor).toString().padStart(fractionDigits + 1, "0");
  const whole = raw.slice(0, raw.length - fractionDigits);
  const fraction = raw.slice(raw.length - fractionDigits);
  const groupedWhole = options.grouping === false ? whole : group(whole);
  let number = fractionDigits > 0 ? `${groupedWhole}.${fraction}` : groupedWhole;
  if (options.digits === "arabic-indic") {
    number = number
      .replace(/\d/g, (d) => ARABIC_INDIC[Number(d)] ?? d)
      .replace(/,/g, "٬")
      .replace(/\./g, "٫");
  }
  const label = isCurrencyCode(currency)
    ? currencyLabel(currency, locale, options.display ?? "symbol")
    : currency;
  const sign = negative ? "-" : "";
  return locale === "ar" || options.labelPlacement === "suffix"
    ? `${sign}${number} ${label}`
    : `${sign}${label} ${number}`;
}
